/**
 * Kontaktpersonen eines Schülers: anzeigen, anlegen, bearbeiten, entfernen.
 *
 * `student_guardians` ist eine n:m-Tabelle, ein Schüler kann also mehrere
 * Angehörige haben. Gefüllt wurde das bisher nur von der Online-Anmeldung und
 * den Importen — Mutter *und* Vater ließen sich in der Verwaltung nicht
 * hinterlegen. Die Kontakte-Karte zeigt außerdem nur eine Person, deshalb
 * blieben schon vorhandene zweite Personen unsichtbar.
 *
 * Der Zahler steht mit in der Liste, weil er dieselbe Art Datensatz ist. Seine
 * Rolle und seine Verknüpfung sind aber festgenagelt: an ihm hängen Vertrag,
 * Rechnungen und SEPA-Mandat.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import PhoneLink from '@/components/PhoneLink';
import EmailLink from '@/components/EmailLink';
import { CONTACT_ROLES, contactRoleLabel } from '@/lib/contactRoles';

const LEERES_FORMULAR = {
  first_name: '',
  last_name: '',
  relationship_type: 'mother',
  phone: '',
  email: '',
};

const feldKlasse =
  'w-full h-8 px-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-red';

function Feld({ label, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
        {label}
      </span>
      <input className={feldKlasse} {...rest} />
    </label>
  );
}

export default function ContactPersonsDialog({
  open,
  onOpenChange,
  studentId,
  studentName,
  guardians = [],
  onChanged,
}) {
  // `null` = niemand in Bearbeitung, 'neu' = leeres Formular, sonst Guardian-ID.
  const [bearbeitet, setBearbeitet] = useState(null);
  const [formular, setFormular] = useState(LEERES_FORMULAR);
  const [entfernenId, setEntfernenId] = useState(null);
  const [läuft, setLäuft] = useState(false);

  // Der Zahler zuerst, danach in der Reihenfolge, in der sie hängen.
  const zeilen = [...guardians].sort(
    (a, b) => Number(!!b.guardian?.is_payer) - Number(!!a.guardian?.is_payer),
  );

  const abbrechen = () => {
    setBearbeitet(null);
    setFormular(LEERES_FORMULAR);
  };

  const beginneNeu = () => {
    setEntfernenId(null);
    setBearbeitet('neu');
    setFormular({ ...LEERES_FORMULAR, last_name: studentName?.last_name || '' });
  };

  const beginneBearbeiten = (zeile) => {
    setEntfernenId(null);
    setBearbeitet(zeile.guardian.id);
    setFormular({
      first_name: zeile.guardian.first_name || '',
      last_name: zeile.guardian.last_name || '',
      relationship_type: zeile.relationship_type || 'parent',
      phone: zeile.guardian.phone || '',
      email: zeile.guardian.email || '',
    });
  };

  const speichern = async () => {
    if (!formular.first_name.trim() || !formular.last_name.trim()) {
      toast.error('Vor- und Nachname sind Pflicht');
      return;
    }
    setLäuft(true);
    try {
      if (bearbeitet === 'neu') {
        await api.post(`/students/${studentId}/guardians`, formular);
        toast.success('Kontaktperson angelegt');
      } else {
        await api.put(`/students/${studentId}/guardians/${bearbeitet}`, formular);
        toast.success('Kontaktperson gespeichert');
      }
      abbrechen();
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLäuft(false);
    }
  };

  const entfernen = async (guardianId) => {
    setLäuft(true);
    try {
      await api.delete(`/students/${studentId}/guardians/${guardianId}`);
      toast.success('Kontaktperson entfernt');
      setEntfernenId(null);
      await onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setLäuft(false);
    }
  };

  const formularBlock = (
    <div className="border border-brand-red/40 bg-red-50/40 rounded p-2 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Feld
          label="Vorname"
          value={formular.first_name}
          onChange={(e) => setFormular({ ...formular, first_name: e.target.value })}
          data-testid="kontakt-vorname"
        />
        <Feld
          label="Nachname"
          value={formular.last_name}
          onChange={(e) => setFormular({ ...formular, last_name: e.target.value })}
          data-testid="kontakt-nachname"
        />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">
            Rolle
          </span>
          <select
            className={feldKlasse}
            value={formular.relationship_type}
            onChange={(e) => setFormular({ ...formular, relationship_type: e.target.value })}
            data-testid="kontakt-rolle"
          >
            {CONTACT_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        <Feld
          label="Telefon"
          value={formular.phone}
          placeholder="mehrere mit Komma"
          onChange={(e) => setFormular({ ...formular, phone: e.target.value })}
          data-testid="kontakt-telefon"
        />
        <Feld
          label="E-Mail"
          type="email"
          value={formular.email}
          onChange={(e) => setFormular({ ...formular, email: e.target.value })}
          data-testid="kontakt-email"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={abbrechen}>
          Abbrechen
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs"
          disabled={läuft}
          onClick={speichern}
          data-testid="kontakt-speichern"
        >
          Speichern
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="contact-persons-dialog"
      >
        {/* Kein eigenes X: DialogContent bringt oben rechts schon eines mit. */}
        <DialogHeader className="px-4 py-3 pr-12 border-b border-slate-200 flex-row items-center space-y-0">
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-4 h-4 text-brand-red" />
            Kontaktpersonen
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 text-xs">
          {zeilen.length === 0 && bearbeitet !== 'neu' && (
            <p className="text-slate-400 py-2">Noch keine Kontaktperson hinterlegt.</p>
          )}

          {zeilen.map((zeile) =>
            bearbeitet === zeile.guardian.id ? (
              <div key={zeile.id}>{formularBlock}</div>
            ) : (
              <div
                key={zeile.id}
                className="border border-slate-200 rounded p-2 flex items-start gap-2"
                data-testid="kontakt-zeile"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">
                      {zeile.guardian.last_name}, {zeile.guardian.first_name}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      {contactRoleLabel(zeile.relationship_type)}
                    </span>
                    {zeile.guardian.is_payer && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                        Zahler
                      </span>
                    )}
                  </div>
                  {zeile.guardian.phone && (
                    <div className="mt-1">
                      <PhoneLink phone={zeile.guardian.phone} />
                    </div>
                  )}
                  {zeile.guardian.email && (
                    <EmailLink email={zeile.guardian.email} className="text-slate-600 truncate" />
                  )}
                </div>
                {entfernenId === zeile.guardian.id ? (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 text-xs"
                      disabled={läuft}
                      onClick={() => entfernen(zeile.guardian.id)}
                      data-testid="kontakt-entfernen-bestaetigen"
                    >
                      Entfernen
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={() => setEntfernenId(null)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => beginneBearbeiten(zeile)}
                      className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                      aria-label={`${zeile.guardian.first_name} ${zeile.guardian.last_name} bearbeiten`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {/* Am Zahler hängen Vertrag, Rechnungen und Mandat — das
                        Backend weist das Entfernen ohnehin ab. */}
                    {!zeile.guardian.is_payer && (
                      <button
                        type="button"
                        onClick={() => setEntfernenId(zeile.guardian.id)}
                        className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        aria-label={`${zeile.guardian.first_name} ${zeile.guardian.last_name} entfernen`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ),
          )}

          {bearbeitet === 'neu' ? (
            formularBlock
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={beginneNeu}
              data-testid="kontakt-hinzufuegen"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Kontaktperson hinzufügen
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
